import { ReduxState } from "ra-core"
import { useCallback, createContext, FC, useContext, useMemo } from "react"
import { useSelector } from "react-redux"
import { BackupJobStatus, DatabaseResponseDto, ValidationJobStatus } from "@/gen/api/backup"
import { AtopFuncArea, SearchField, FilterField, FilterOption } from "../list/datagridFunctionWidgets"


interface BackupListFilterControllerProps {
  AtopFilters: FC,
}

const BackupListFilterContext = createContext<BackupListFilterControllerProps>({
  AtopFilters: () => null,
})


export const BackupListFilterContextProvider: FC<{ flavor: 'backups' | 'validates' }> = ({ children, flavor }) => {
  const value = useBackupListFilterController(flavor)
  return (
    <BackupListFilterContext.Provider value={value}>
      {children}
    </BackupListFilterContext.Provider>
  )
}

const statusOptionsBag = {
  'backups': [
    { label: 'No Backup', value: BackupJobStatus.NoBackup },
    { label: 'In Progress', value: BackupJobStatus.InProgress },
    { label: 'Failed', value: BackupJobStatus.Failed },
    { label: 'Received', value: BackupJobStatus.Received },
  ],
  'validates': [
    { label: 'No Validation', value: ValidationJobStatus.NoValidation },
    { label: 'In Progress', value: ValidationJobStatus.InProgress },
    { label: 'Validated', value: ValidationJobStatus.Validated },
    { label: 'Failed', value: ValidationJobStatus.Failed },
  ]
}

const useBackupListFilterController = (flavor: 'backups' | 'validates'): BackupListFilterControllerProps => {

  const data = useSelector<ReduxState, Record<number, DatabaseResponseDto>>(store => store.admin.resources['databases'].data as any)
  const dbList = Object.values(data || {});
  
  const engineOptions = useMemo(() => Object.values(
    dbList.reduce((acc, d) => {
      if (d.engineId) {
        const option = ({
          label: `${d.engineVersion || ''}${d.engineEdition ? ' ' + d.engineEdition : ''}`,
          value: d.engineId
        })
        acc[d.engineId] = option
      }
      return acc
    }, {} as Record<number, {label:string,value:number}>)
  ).sort((a, b) => a.label < b.label ? -1 : 1), [ JSON.stringify([...new Set(dbList.map(d => d.engineId))].sort()) ])
  
  const statusOptions = statusOptionsBag[flavor]

  
  const AtopFilters = useCallback(() => (
    <AtopFuncArea>
      <SearchField 
        sourceKey="dbName"
        match={(d: DatabaseResponseDto, fval:string) => !!d.name && d.name.toLowerCase().includes(fval.trim().toLowerCase())}
        sx={{ width: 280 }}
        TextFieldProps={{ placeholder: 'Search by Database Name' }}
      />
      <FilterField 
        label="Engine" 
        filterOptions={engineOptions} 
        sourceKey="engine"
        match={(d: DatabaseResponseDto, fval:FilterOption) => d.engineId === fval.value}
        sx={{ width: 320 }}
      />
      <FilterField 
        label="Status" 
        filterOptions={statusOptions} 
        sourceKey="status"
        match={(d: DatabaseResponseDto, fval:FilterOption) => 
          flavor === 'backups' 
          ? d.backupJobStatus === fval.value
          : d.validationJobStatus === fval.value}
        sx={{ width: 160 }}
      />
    </AtopFuncArea>
  ), [engineOptions, statusOptions])
  
  return {
    AtopFilters,
  }
}

export const useBackupListFilterContext = () => useContext(BackupListFilterContext);

